import type { ChangeEvent, RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const MARKDOWN_EDITOR_MIN_HEIGHT = 144;
export const MARKDOWN_EDITOR_MAX_HEIGHT = 288;

function clampEditorHeight(nextHeight: number): number {
    return Math.max(MARKDOWN_EDITOR_MIN_HEIGHT, Math.min(MARKDOWN_EDITOR_MAX_HEIGHT, nextHeight));
}

function getEditorHeight(textarea: HTMLTextAreaElement, editorRoot: HTMLDivElement | null): number {
    const editorElement = editorRoot?.querySelector('.w-md-editor');
    const editorChromeHeight =
        editorElement instanceof HTMLElement
            ? editorElement.offsetHeight - textarea.offsetHeight
            : 0;

    return clampEditorHeight(textarea.scrollHeight + editorChromeHeight);
}

export function useMarkdownEditorAutoHeight({
    value,
    isDisabled,
}: {
    value: string;
    isDisabled: boolean;
}): {
    editorHeight: number;
    editorRootRef: RefObject<HTMLDivElement>;
    handleTextareaChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
} {
    const [editorHeight, setEditorHeight] = useState(MARKDOWN_EDITOR_MIN_HEIGHT);
    const editorRootRef = useRef<HTMLDivElement>(null);

    const syncEditorHeight = useCallback((textarea: HTMLTextAreaElement) => {
        const nextHeight = getEditorHeight(textarea, editorRootRef.current);
        setEditorHeight((currentHeight) =>
            currentHeight === nextHeight ? currentHeight : nextHeight
        );
    }, []);

    useLayoutEffect(() => {
        if (isDisabled) {
            return;
        }

        const textarea = editorRootRef.current?.querySelector('.w-md-editor-text-input');
        if (textarea instanceof HTMLTextAreaElement) {
            syncEditorHeight(textarea);
        }
    }, [isDisabled, syncEditorHeight, value]);

    const handleTextareaChange = useCallback(
        (event: ChangeEvent<HTMLTextAreaElement>) => {
            syncEditorHeight(event.currentTarget);
        },
        [syncEditorHeight]
    );

    return { editorHeight, editorRootRef, handleTextareaChange };
}
